import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Coffee, PenTool, Lightbulb, AlertCircle, Clock, Users, DollarSign, TrendingDown, Flame, Play, Square, Download, Share2, Volume2, VolumeX, Zap, Timer, FileText, Sliders, X, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

// Font loader - hand-drawn style fonts
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&family=Patrick+Hand&family=Inter:wght@300;400;500&display=swap');

    .font-handwritten {
      font-family: 'Caveat', cursive;
    }
    .font-sketch {
      font-family: 'Patrick Hand', cursive;
    }
    .font-clean {
      font-family: 'Inter', sans-serif;
    }

    /* Paper texture */
    .paper-bg {
      background-color: #f5f0e1;
      background-image:
        linear-gradient(90deg, transparent 79px, #e8dcc8 79px, #e8dcc8 81px, transparent 81px),
        linear-gradient(#e8dcc8 1px, transparent 1px);
      background-size: 100% 28px;
    }

    /* Coffee stain */
    .coffee-stain {
      background: radial-gradient(ellipse at center,
        rgba(139, 90, 43, 0.15) 0%,
        rgba(139, 90, 43, 0.08) 40%,
        rgba(139, 90, 43, 0.03) 60%,
        transparent 70%);
    }

    /* Sketchy underline */
    .sketchy-underline {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='8'%3E%3Cpath d='M0 4 Q25 0 50 4 T100 4' stroke='%23dc2626' stroke-width='2' fill='none'/%3E%3C/svg%3E");
      background-repeat: repeat-x;
      background-position: bottom;
      background-size: 100px 8px;
    }

    /* Wobbly border */
    .sketchy-border {
      border-radius: 255px 15px 225px 15px/15px 225px 15px 255px;
    }

    /* Pencil stroke effect */
    .pencil-stroke {
      filter: url(#pencil);
    }
  `}</style>
);

// SVG filter for pencil effect
const PencilFilter = () => (
  <svg style={{ position: 'absolute', width: 0, height: 0 }}>
    <defs>
      <filter id="pencil">
        <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="5" result="noise" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="1" />
      </filter>
    </defs>
  </svg>
);

// ============================================
// SOUND DESIGN SYSTEM
// ============================================
class SoundEngine {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  private getContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  isEnabled() {
    return this.enabled;
  }

  // Cash register "ka-ching" sound
  playCashRegister() {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      const now = ctx.currentTime;

      // Bell sound
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.setValueAtTime(2000, now);
      osc.frequency.exponentialRampToValueAtTime(1500, now + 0.1);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

      osc.start(now);
      osc.stop(now + 0.2);
    } catch (e) {}
  }

  // Soft tick sound
  playTick() {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      const now = ctx.currentTime;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.setValueAtTime(800, now);
      gain.gain.setValueAtTime(0.05, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

      osc.start(now);
      osc.stop(now + 0.05);
    } catch (e) {}
  }

  // Paper crumple sound
  playPaperCrumple() {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      const now = ctx.currentTime;

      // White noise burst
      const bufferSize = ctx.sampleRate * 0.15;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.3));
      }

      const source = ctx.createBufferSource();
      const filter = ctx.createBiquadFilter();
      const gain = ctx.createGain();

      source.buffer = buffer;
      filter.type = 'highpass';
      filter.frequency.value = 2000;

      source.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      gain.gain.setValueAtTime(0.08, now);

      source.start(now);
    } catch (e) {}
  }

  // Success chime
  playSuccess() {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      const now = ctx.currentTime;

      [523.25, 659.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + i * 0.1);
        gain.gain.linearRampToValueAtTime(0.08, now + i * 0.1 + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.3);

        osc.start(now + i * 0.1);
        osc.stop(now + i * 0.1 + 0.3);
      });
    } catch (e) {}
  }

  // Alert/warning sound
  playAlert() {
    if (!this.enabled) return;
    try {
      const ctx = this.getContext();
      const now = ctx.currentTime;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.setValueAtTime(440, now);
      osc.frequency.setValueAtTime(520, now + 0.1);
      osc.frequency.setValueAtTime(440, now + 0.2);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

      osc.start(now);
      osc.stop(now + 0.3);
    } catch (e) {}
  }
}

const soundEngine = new SoundEngine();

// ============================================
// DURING MEETING MODE COMPONENT
// ============================================
interface LiveMeetingModeProps {
  isOpen: boolean;
  onClose: () => void;
  attendees: number;
  hourlyRate: number;
  currency: CurrencyCode;
  soundEnabled: boolean;
}

const LiveMeetingMode: React.FC<LiveMeetingModeProps> = ({
  isOpen,
  onClose,
  attendees,
  hourlyRate,
  currency,
  soundEnabled
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const lastCashSound = useRef(0);

  const curr = currencies[currency];

  // Cost per second for all attendees
  const costPerSecond = (attendees * hourlyRate) / 3600;
  const currentCost = elapsedSeconds * costPerSecond;
  const recoveryCost = attendees * (hourlyRate * (23.25 / 60));
  const totalCost = currentCost + recoveryCost;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning && startTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedSeconds(elapsed);

        // Play cash sound every $10 spent
        const costThreshold = Math.floor(currentCost / 10);
        if (costThreshold > lastCashSound.current && soundEnabled) {
          soundEngine.playCashRegister();
          lastCashSound.current = costThreshold;
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isRunning, startTime, currentCost, soundEnabled]);

  const handleStart = () => {
    setIsRunning(true);
    setStartTime(Date.now());
    setElapsedSeconds(0);
    lastCashSound.current = 0;
    if (soundEnabled) soundEngine.playTick();
  };

  const handleStop = () => {
    setIsRunning(false);
    if (soundEnabled) soundEngine.playAlert();
  };

  const handleReset = () => {
    setIsRunning(false);
    setElapsedSeconds(0);
    setStartTime(null);
    lastCashSound.current = 0;
  };

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-gradient-to-br from-stone-900 via-red-950 to-stone-900 flex items-center justify-center"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/60 hover:text-white p-2"
      >
        <X className="w-8 h-8" />
      </button>

      <div className="text-center px-8">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <Timer className="w-6 h-6 text-red-400" />
            <span className="font-sketch text-red-400 text-lg uppercase tracking-wider">
              {isRunning ? 'Meeting in Progress' : 'Ready to Track'}
            </span>
          </div>
          <p className="text-white/40 font-sketch">
            {attendees} people × {curr.symbol}{hourlyRate}/hr
          </p>
        </motion.div>

        {/* Giant Cost Display */}
        <motion.div
          className="mb-8"
          animate={{ scale: isRunning ? [1, 1.02, 1] : 1 }}
          transition={{ duration: 1, repeat: isRunning ? Infinity : 0 }}
        >
          <div className="text-white/40 font-sketch text-lg mb-2">this meeting has cost</div>
          <div className="font-handwritten text-8xl md:text-[12rem] text-white leading-none">
            {curr.symbol}{Math.floor(currentCost).toLocaleString()}
            <span className="text-4xl md:text-6xl text-white/40">
              .{String(Math.floor((currentCost % 1) * 100)).padStart(2, '0')}
            </span>
          </div>
          <div className="text-red-400/80 font-sketch text-sm mt-4">
            + {curr.symbol}{Math.round(recoveryCost)} recovery cost pending
          </div>
        </motion.div>

        {/* Timer */}
        <div className="mb-12">
          <div className="font-mono text-4xl md:text-6xl text-white/80 tracking-wider">
            {formatTime(elapsedSeconds)}
          </div>
        </div>

        {/* Progress bar showing money burning */}
        <div className="w-full max-w-md mx-auto mb-12">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500"
              style={{ width: `${Math.min((currentCost / 500) * 100, 100)}%` }}
              animate={{ opacity: isRunning ? [0.7, 1, 0.7] : 1 }}
              transition={{ duration: 0.5, repeat: isRunning ? Infinity : 0 }}
            />
          </div>
          <div className="flex justify-between text-white/30 text-xs font-sketch mt-1">
            <span>{curr.symbol}0</span>
            <span>{curr.symbol}500</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          {!isRunning ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStart}
              className="flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-500 text-white font-sketch text-xl rounded-full"
            >
              <Play className="w-6 h-6" />
              Start Meeting
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStop}
              className="flex items-center gap-3 px-8 py-4 bg-white text-red-600 font-sketch text-xl rounded-full"
            >
              <Square className="w-6 h-6" />
              End Meeting
            </motion.button>
          )}

          {elapsedSeconds > 0 && !isRunning && (
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.05 }}
              onClick={handleReset}
              className="px-6 py-4 bg-white/10 text-white font-sketch rounded-full"
            >
              Reset
            </motion.button>
          )}
        </div>

        {/* Tip */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-white/30 font-sketch text-sm mt-12 max-w-md mx-auto"
        >
          Pro tip: Share your screen with this open to keep meetings focused
        </motion.p>
      </div>
    </motion.div>
  );
};

// ============================================
// MEETING INVOICE COMPONENT
// ============================================
interface MeetingInvoiceProps {
  isOpen: boolean;
  onClose: () => void;
  attendees: number;
  duration: number;
  costs: {
    hourlyRate: number;
    directCost: number;
    recoveryCost: number;
    productivityCost: number;
    totalPerMeeting: number;
  };
  currency: CurrencyCode;
  soundEnabled: boolean;
}

const MeetingInvoice: React.FC<MeetingInvoiceProps> = ({
  isOpen,
  onClose,
  attendees,
  duration,
  costs,
  currency,
  soundEnabled
}) => {
  const [copied, setCopied] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const curr = currencies[currency];

  const invoiceNumber = useMemo(() => Math.floor(Math.random() * 9000) + 1000, []);
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const handleShare = async () => {
    const text = `Meeting Tax Invoice #${invoiceNumber}\n\nAttendees: ${attendees}\nDuration: ${duration}hr\n\nDirect Cost: ${curr.symbol}${costs.directCost}\nRecovery Cost: ${curr.symbol}${costs.recoveryCost}\nProductivity Tax: ${curr.symbol}${costs.productivityCost}\n\nTOTAL: ${curr.symbol}${costs.totalPerMeeting}\n\n"This meeting could have been an email."\n\nCalculate yours: ${window.location.href}`;

    if (navigator.share) {
      try {
        await navigator.share({ text });
        if (soundEnabled) soundEngine.playSuccess();
      } catch (e) {}
    } else {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (soundEnabled) soundEngine.playSuccess();
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    if (isOpen && soundEnabled) {
      soundEngine.playPaperCrumple();
    }
  }, [isOpen, soundEnabled]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20, rotate: -2 }}
        animate={{ scale: 1, y: 0, rotate: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        ref={invoiceRef}
        className="bg-white max-w-md w-full shadow-2xl relative"
        style={{
          background: 'linear-gradient(to bottom, #fff 0%, #f9f7f4 100%)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)'
        }}
      >
        {/* Torn paper top edge */}
        <div className="absolute -top-3 left-0 right-0 h-4 bg-white"
          style={{
            maskImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 100 10\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 10 Q5 5 10 10 T20 10 T30 10 T40 10 T50 10 T60 10 T70 10 T80 10 T90 10 T100 10 L100 0 L0 0 Z\' fill=\'white\'/%3E%3C/svg%3E")',
            maskSize: '50px 100%'
          }}
        />

        <div className="p-8 font-mono text-sm">
          {/* Header */}
          <div className="border-b-2 border-dashed border-stone-300 pb-4 mb-4">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-bold text-xl text-stone-800">MEETING TAX</h2>
                <p className="text-stone-500 text-xs">Invoice #{invoiceNumber}</p>
              </div>
              <div className="text-right text-stone-500 text-xs">
                <p>{date}</p>
                <p>Due: Immediately</p>
              </div>
            </div>
          </div>

          {/* Meeting Details */}
          <div className="mb-4 text-stone-600">
            <p className="font-bold text-stone-800">Weekly Planning Meeting</p>
            <p className="text-xs text-stone-500">{attendees} attendees × {duration} hour{duration > 1 ? 's' : ''}</p>
          </div>

          {/* Line Items */}
          <div className="border-t border-b border-stone-200 py-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-stone-600">Direct time cost</span>
              <span className="text-stone-800">{curr.symbol}{costs.directCost.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-stone-500">
              <span className="text-xs pl-4">({attendees} × {curr.symbol}{costs.hourlyRate}/hr × {duration}hr)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">Recovery time (23 min × {attendees})</span>
              <span className="text-stone-800">{curr.symbol}{costs.recoveryCost.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">Productivity tax (40%)</span>
              <span className="text-stone-800">{curr.symbol}{costs.productivityCost.toLocaleString()}</span>
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-between items-center py-4 border-b-2 border-double border-stone-300">
            <span className="font-bold text-lg text-stone-800">TOTAL DUE</span>
            <span className="font-bold text-2xl text-red-600">
              {curr.symbol}{costs.totalPerMeeting.toLocaleString()}
            </span>
          </div>

          {/* Quote */}
          <div className="mt-6 text-center italic text-stone-500 text-xs">
            "This meeting could have been an email."
            <br />
            <span className="text-stone-400">— Everyone, always</span>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleShare}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-stone-800 text-white rounded font-sans text-sm"
            >
              {copied ? <CheckCircle className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Share Invoice'}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="px-4 py-3 bg-stone-100 text-stone-600 rounded font-sans text-sm"
            >
              Close
            </motion.button>
          </div>
        </div>

        {/* Bottom tear */}
        <div className="h-4 bg-gradient-to-b from-transparent to-stone-100"
          style={{
            maskImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 100 10\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0 Q5 5 10 0 T20 0 T30 0 T40 0 T50 0 T60 0 T70 0 T80 0 T90 0 T100 0 L100 10 L0 10 Z\' fill=\'white\'/%3E%3C/svg%3E")',
            maskSize: '50px 100%'
          }}
        />
      </motion.div>
    </motion.div>
  );
};

// ============================================
// WHAT IF OPTIMIZER COMPONENT
// ============================================
interface WhatIfOptimizerProps {
  attendees: number;
  duration: number;
  costs: {
    totalPerMeeting: number;
    hourlyRate: number;
  };
  currency: CurrencyCode;
  soundEnabled: boolean;
}

const WhatIfOptimizer: React.FC<WhatIfOptimizerProps> = ({
  attendees,
  duration,
  costs,
  currency,
  soundEnabled
}) => {
  const [shorterDuration, setShorterDuration] = useState(false);
  const [fewerPeople, setFewerPeople] = useState(false);
  const [asyncInstead, setAsyncInstead] = useState(false);

  const curr = currencies[currency];
  const hourlyRate = costs.hourlyRate;
  const recoveryTime = 23.25 / 60;
  const productivityTax = 0.4;

  // Calculate optimized costs
  const optimizedCosts = useMemo(() => {
    let newDuration = duration;
    let newAttendees = attendees;

    if (shorterDuration) {
      newDuration = Math.max(0.25, duration * 0.5); // 50% shorter
    }
    if (fewerPeople) {
      newAttendees = Math.max(2, attendees - 2); // 2 fewer people
    }
    if (asyncInstead) {
      return { newTotal: 0, savings: costs.totalPerMeeting };
    }

    const directCost = newAttendees * hourlyRate * newDuration;
    const recoveryCost = newAttendees * hourlyRate * recoveryTime;
    const productivityCost = newAttendees * hourlyRate * productivityTax;
    const newTotal = Math.round(directCost + recoveryCost + productivityCost);
    const savings = costs.totalPerMeeting - newTotal;

    return { newTotal, savings };
  }, [shorterDuration, fewerPeople, asyncInstead, attendees, duration, hourlyRate, costs.totalPerMeeting]);

  const handleToggle = (setter: React.Dispatch<React.SetStateAction<boolean>>, current: boolean) => {
    setter(!current);
    if (soundEnabled) {
      if (!current) soundEngine.playSuccess();
      else soundEngine.playTick();
    }
  };

  const totalSavingsPercent = Math.round((optimizedCosts.savings / costs.totalPerMeeting) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-stone-200 sketchy-border"
    >
      <div className="flex items-center gap-2 mb-6">
        <Sliders className="w-5 h-5 text-green-600" />
        <span className="font-handwritten text-2xl text-stone-700">what if...</span>
      </div>

      {/* Toggle Options */}
      <div className="space-y-4 mb-6">
        {/* Shorter Duration */}
        <label className="flex items-center justify-between cursor-pointer group">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-6 rounded-full p-1 transition-colors ${shorterDuration ? 'bg-green-500' : 'bg-stone-300'}`}
              onClick={() => handleToggle(setShorterDuration, shorterDuration)}
            >
              <motion.div
                className="w-4 h-4 bg-white rounded-full shadow"
                animate={{ x: shorterDuration ? 24 : 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </div>
            <span className="font-sketch text-stone-700 group-hover:text-stone-900">
              Make it {Math.round(duration * 30)} min instead of {duration * 60} min?
            </span>
          </div>
          {shorterDuration && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="font-handwritten text-green-600"
            >
              -{curr.symbol}{Math.round(costs.totalPerMeeting * 0.3)}
            </motion.span>
          )}
        </label>

        {/* Fewer People */}
        <label className="flex items-center justify-between cursor-pointer group">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-6 rounded-full p-1 transition-colors ${fewerPeople ? 'bg-green-500' : 'bg-stone-300'}`}
              onClick={() => handleToggle(setFewerPeople, fewerPeople)}
            >
              <motion.div
                className="w-4 h-4 bg-white rounded-full shadow"
                animate={{ x: fewerPeople ? 24 : 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </div>
            <span className="font-sketch text-stone-700 group-hover:text-stone-900">
              Invite 2 fewer people?
            </span>
          </div>
          {fewerPeople && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="font-handwritten text-green-600"
            >
              -{curr.symbol}{Math.round(costs.totalPerMeeting * 0.33)}
            </motion.span>
          )}
        </label>

        {/* Async Instead */}
        <label className="flex items-center justify-between cursor-pointer group">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-6 rounded-full p-1 transition-colors ${asyncInstead ? 'bg-green-500' : 'bg-stone-300'}`}
              onClick={() => handleToggle(setAsyncInstead, asyncInstead)}
            >
              <motion.div
                className="w-4 h-4 bg-white rounded-full shadow"
                animate={{ x: asyncInstead ? 24 : 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </div>
            <span className="font-sketch text-stone-700 group-hover:text-stone-900">
              Make it async (Slack/email)?
            </span>
          </div>
          {asyncInstead && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="font-handwritten text-green-600"
            >
              -{curr.symbol}{costs.totalPerMeeting} (100%!)
            </motion.span>
          )}
        </label>
      </div>

      {/* Savings Summary */}
      <AnimatePresence>
        {optimizedCosts.savings > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="pt-4 border-t border-dashed border-stone-300"
          >
            <div className="flex justify-between items-center">
              <span className="font-sketch text-stone-600">You'd save:</span>
              <div className="text-right">
                <span className="font-handwritten text-3xl text-green-600">
                  {curr.symbol}{optimizedCosts.savings.toLocaleString()}
                </span>
                <span className="font-sketch text-green-600 text-sm ml-2">
                  ({totalSavingsPercent}%)
                </span>
              </div>
            </div>
            {!asyncInstead && optimizedCosts.newTotal > 0 && (
              <p className="font-sketch text-xs text-stone-500 mt-2">
                New meeting cost: {curr.symbol}{optimizedCosts.newTotal.toLocaleString()}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Animated scribble counter
const ScribbleNumber = ({ value, prefix = '' }: { value: number; prefix?: string }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);
    const duration = 600;
    const startTime = Date.now();
    const startValue = displayValue;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(startValue + (value - startValue) * eased));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [value]);

  return (
    <motion.span
      className="font-handwritten"
      animate={{ rotate: isAnimating ? [0, -1, 1, 0] : 0 }}
      transition={{ duration: 0.1 }}
    >
      {prefix}{displayValue.toLocaleString()}
    </motion.span>
  );
};

// Doodle components
const DoodleArrow = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="60" height="30" viewBox="0 0 60 30">
    <path
      d="M5 15 Q15 10 25 15 T45 15 L55 15 M45 8 L55 15 L45 22"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const DoodleCircle = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="100" height="100" viewBox="0 0 100 100">
    <ellipse
      cx="50" cy="50" rx="45" ry="40"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeDasharray="5,3"
      transform="rotate(-5 50 50)"
    />
  </svg>
);

const DoodleStar = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="30" height="30" viewBox="0 0 30 30">
    <path
      d="M15 2 L18 12 L28 12 L20 18 L23 28 L15 22 L7 28 L10 18 L2 12 L12 12 Z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

// Sticky note component
const StickyNote = ({ children, color = 'yellow', rotation = -2, className = '' }: {
  children: React.ReactNode;
  color?: 'yellow' | 'pink' | 'blue';
  rotation?: number;
  className?: string;
}) => {
  const colors = {
    yellow: 'bg-yellow-200 border-yellow-300',
    pink: 'bg-pink-200 border-pink-300',
    blue: 'bg-blue-200 border-blue-300',
  };

  return (
    <motion.div
      className={`${colors[color]} p-4 shadow-md border-b-4 ${className}`}
      style={{
        transform: `rotate(${rotation}deg)`,
        boxShadow: '2px 2px 5px rgba(0,0,0,0.1), -1px -1px 2px rgba(0,0,0,0.05)'
      }}
      whileHover={{ scale: 1.05, rotate: 0 }}
    >
      {children}
    </motion.div>
  );
};

// Currency options with PPP-adjusted salary ranges
// Based on 2024 research: US senior SWE ~$150-250k, India senior ~₹20-50 LPA, UK senior ~£70-130k
const currencies = {
  USD: {
    symbol: '$',
    flag: '🇺🇸',
    name: 'USD',
    salaryMin: 50000,
    salaryMax: 300000,
    salaryDefault: 120000,
    salaryStep: 10000,
    salaryThreshold: 100000, // "that's a salary" threshold
    formatSalary: (val: number) => `$${Math.round(val / 1000)}k`,
    formatLabel: (val: number) => `$${Math.round(val / 1000)}k`,
  },
  INR: {
    symbol: '₹',
    flag: '🇮🇳',
    name: 'INR',
    salaryMin: 500000,    // 5 LPA
    salaryMax: 6000000,   // 60 LPA
    salaryDefault: 2000000, // 20 LPA (senior SWE)
    salaryStep: 100000,
    salaryThreshold: 1500000, // 15 LPA threshold
    formatSalary: (val: number) => `₹${(val / 100000).toFixed(1)} LPA`,
    formatLabel: (val: number) => `${Math.round(val / 100000)}L`,
  },
  GBP: {
    symbol: '£',
    flag: '🇬🇧',
    name: 'GBP',
    salaryMin: 30000,
    salaryMax: 200000,
    salaryDefault: 70000,
    salaryStep: 5000,
    salaryThreshold: 60000, // £60k threshold
    formatSalary: (val: number) => `£${Math.round(val / 1000)}k`,
    formatLabel: (val: number) => `£${Math.round(val / 1000)}k`,
  },
};

type CurrencyCode = keyof typeof currencies;

// Helper to format numbers with appropriate separators
const formatCurrencyValue = (value: number, currencyCode: CurrencyCode): string => {
  const curr = currencies[currencyCode];
  if (currencyCode === 'INR') {
    // Indian numbering: lakhs and crores
    if (value >= 10000000) {
      return `${curr.symbol}${(value / 10000000).toFixed(1)} Cr`;
    } else if (value >= 100000) {
      return `${curr.symbol}${(value / 100000).toFixed(1)}L`;
    }
    return `${curr.symbol}${value.toLocaleString('en-IN')}`;
  }
  return `${curr.symbol}${value.toLocaleString()}`;
};

// Duration options with sketchy labels
const durationOptions = [
  { label: '15m', value: 0.25, sketch: 'quick sync' },
  { label: '30m', value: 0.5, sketch: 'standup' },
  { label: '1hr', value: 1, sketch: 'planning' },
  { label: '2hr', value: 2, sketch: 'all-hands' },
];

const MeetingTaxStudy = () => {
  const navigate = useNavigate();

  // Calculator state
  const [attendees, setAttendees] = useState(6);
  const [duration, setDuration] = useState(1);
  const [avgSalary, setAvgSalary] = useState(120000);
  const [frequency, setFrequency] = useState(52);
  const [currency, setCurrency] = useState<CurrencyCode>('USD');

  // Feature states
  const [showLiveMeeting, setShowLiveMeeting] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Sync sound engine with state
  useEffect(() => {
    soundEngine.setEnabled(soundEnabled);
  }, [soundEnabled]);

  // Get current currency info
  const curr = currencies[currency];

  // Update salary when currency changes to use appropriate local range
  useEffect(() => {
    setAvgSalary(curr.salaryDefault);
  }, [currency]);

  // Live counter for industry waste
  const [liveWaste, setLiveWaste] = useState(0);

  useEffect(() => {
    const perSecond = 37000000000 / (365 * 24 * 60 * 60);
    const interval = setInterval(() => {
      setLiveWaste(prev => prev + perSecond / 10);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Calculate costs using local salary (PPP-adjusted per currency)
  const costs = useMemo(() => {
    const hourlyRate = avgSalary / 2080; // Standard work hours per year
    const recoveryTime = 23.25 / 60; // 23.25 minutes in hours
    const productivityTax = 0.4; // 40% productivity loss

    const directCost = attendees * hourlyRate * duration;
    const recoveryCost = attendees * hourlyRate * recoveryTime;
    const productivityCost = attendees * hourlyRate * productivityTax;
    const totalPerMeeting = directCost + recoveryCost + productivityCost;
    const yearlyTotal = totalPerMeeting * frequency;

    return {
      hourlyRate: Math.round(hourlyRate),
      directCost: Math.round(directCost),
      recoveryCost: Math.round(recoveryCost),
      productivityCost: Math.round(productivityCost),
      totalPerMeeting: Math.round(totalPerMeeting),
      yearlyTotal: Math.round(yearlyTotal),
    };
  }, [attendees, duration, avgSalary, frequency]);

  return (
    <div className="min-h-screen paper-bg font-clean relative overflow-hidden">
      <FontLoader />
      <PencilFilter />

      {/* Coffee stain decorations */}
      <div className="coffee-stain absolute top-20 right-20 w-48 h-48 opacity-60" />
      <div className="coffee-stain absolute bottom-40 left-10 w-32 h-32 opacity-40" />

      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-6">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/labs')}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors font-sketch"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">back to labs</span>
        </motion.button>

        {/* Right side controls */}
        <div className="flex items-center gap-3">
          {/* Sound Toggle */}
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              if (!soundEnabled) soundEngine.playTick();
            }}
            className={`p-2 rounded-full transition-all ${
              soundEnabled
                ? 'bg-green-100 text-green-600 border border-green-200'
                : 'bg-stone-100 text-stone-400 border border-stone-200'
            }`}
            title={soundEnabled ? 'Sound on' : 'Sound off'}
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </motion.button>

          {/* Currency Selector - Sketchy style */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-1 bg-white/80 backdrop-blur-sm rounded-full px-2 py-1 border border-stone-200 sketchy-border"
          >
            {(Object.keys(currencies) as CurrencyCode[]).map((code) => (
              <button
                key={code}
                onClick={() => setCurrency(code)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full font-sketch text-sm transition-all ${
                  currency === code
                    ? 'bg-red-100 text-red-700 border border-red-200'
                    : 'text-stone-500 hover:text-stone-700 hover:bg-stone-100'
                }`}
              >
                <span>{currencies[code].flag}</span>
                <span>{currencies[code].name}</span>
              </button>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Hero - Napkin style */}
      <section className="pt-28 pb-12 px-6 relative">
        <div className="max-w-4xl mx-auto">
          {/* Torn paper edge effect at top */}
          <div className="absolute top-20 left-0 right-0 h-4 bg-gradient-to-b from-stone-300/20 to-transparent"
               style={{ maskImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 100 10\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 10 Q5 0 10 10 T20 10 T30 10 T40 10 T50 10 T60 10 T70 10 T80 10 T90 10 T100 10\' fill=\'white\'/%3E%3C/svg%3E")' }} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative"
          >
            {/* Doodle annotations */}
            <div className="absolute -left-16 top-10 text-red-500 hidden lg:block">
              <DoodleArrow className="rotate-12" />
              <span className="font-handwritten text-sm block -mt-1 ml-2">important!</span>
            </div>

            {/* Title - handwritten style */}
            <div className="mb-2 flex items-center gap-3">
              <span className="font-sketch text-stone-500 text-lg">Lab #09</span>
              <span className="text-stone-300">|</span>
              <span className="font-sketch text-red-600 text-sm">* do the math *</span>
            </div>

            <h1 className="font-handwritten text-6xl md:text-8xl text-stone-800 leading-none mb-4">
              The Meeting
              <span className="relative inline-block ml-4">
                <span className="text-red-600">Tax</span>
                <DoodleCircle className="absolute -inset-4 text-red-400 opacity-60" />
              </span>
            </h1>

            <p className="font-sketch text-xl md:text-2xl text-stone-600 max-w-xl mb-8">
              "What if we calculated how much that standup
              <span className="sketchy-underline px-1">actually</span> cost?"
            </p>

            {/* Live waste counter - sticky note style (US stat, always in USD) */}
            <StickyNote color="pink" rotation={-3} className="inline-block">
              <div className="font-sketch text-xs text-pink-700 mb-1">
                <Flame className="w-3 h-3 inline mr-1" />
                burning since you arrived:
              </div>
              <div className="font-handwritten text-3xl text-red-600">
                $<ScribbleNumber value={Math.round(liveWaste)} />
              </div>
              <div className="font-sketch text-xs text-pink-600 mt-1">
                (US companies, unnecessary meetings)
              </div>
            </StickyNote>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section - Notebook style */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8">

            {/* Input Panel - Left side of napkin */}
            <motion.div
              className="lg:col-span-2 relative"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-stone-200 sketchy-border">

                {/* Hand-drawn header */}
                <div className="flex items-center gap-2 mb-6">
                  <span className="font-handwritten text-2xl text-stone-700">your meeting:</span>
                  <DoodleStar className="text-yellow-500 w-5 h-5" />
                </div>

                {/* Attendees - tally marks style */}
                <div className="mb-8">
                  <label className="font-sketch text-stone-600 text-sm flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4" />
                    people in the room
                  </label>

                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="2"
                      max="20"
                      value={attendees}
                      onChange={(e) => setAttendees(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                    />
                    <div className="font-handwritten text-4xl text-stone-800 w-16 text-center">
                      {attendees}
                    </div>
                  </div>

                  {/* Tally marks visualization */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {Array.from({ length: attendees }).map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                        className={`w-1 h-4 bg-stone-400 ${(i + 1) % 5 === 0 ? 'rotate-45 -ml-3 mr-2' : ''}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Duration - button style */}
                <div className="mb-8">
                  <label className="font-sketch text-stone-600 text-sm flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" />
                    how long?
                  </label>

                  <div className="grid grid-cols-2 gap-2">
                    {durationOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setDuration(opt.value)}
                        className={`p-3 rounded-lg text-left transition-all sketchy-border ${
                          duration === opt.value
                            ? 'bg-red-100 border-2 border-red-400'
                            : 'bg-white border-2 border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className="font-handwritten text-2xl text-stone-800">{opt.label}</div>
                        <div className="font-sketch text-xs text-stone-500">{opt.sketch}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Salary slider - PPP adjusted per currency */}
                <div className="mb-8">
                  <label className="font-sketch text-stone-600 text-sm flex items-center gap-2 mb-3">
                    <DollarSign className="w-4 h-4" />
                    avg salary (rough guess)
                  </label>

                  <input
                    type="range"
                    min={curr.salaryMin}
                    max={curr.salaryMax}
                    step={curr.salaryStep}
                    value={avgSalary}
                    onChange={(e) => setAvgSalary(parseInt(e.target.value))}
                    className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                  />

                  <div className="flex justify-between mt-2">
                    <span className="font-sketch text-xs text-stone-400">{curr.formatLabel(curr.salaryMin)}</span>
                    <span className="font-handwritten text-xl text-stone-700">{curr.formatSalary(avgSalary)}</span>
                    <span className="font-sketch text-xs text-stone-400">{curr.formatLabel(curr.salaryMax)}</span>
                  </div>
                </div>

                {/* Frequency */}
                <div>
                  <label className="font-sketch text-stone-600 text-sm flex items-center gap-2 mb-3">
                    how often?
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'once', value: 1 },
                      { label: 'weekly', value: 52 },
                      { label: 'daily', value: 260 },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setFrequency(opt.value)}
                        className={`px-4 py-2 font-sketch text-sm transition-all ${
                          frequency === opt.value
                            ? 'bg-red-500 text-white rounded-full'
                            : 'text-stone-600 hover:text-stone-900'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Annotation arrow */}
              <div className="absolute -right-8 top-1/2 hidden lg:block text-red-500">
                <DoodleArrow />
              </div>
            </motion.div>

            {/* Results Panel - Right side, the "aha!" moment */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Main cost - BIG handwritten number */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 shadow-lg border-2 border-red-200 sketchy-border relative overflow-hidden">

                {/* Scribbled "TOTAL" label */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-sketch text-stone-500">the damage:</span>
                  <span className="font-sketch text-xs text-red-500 bg-red-100 px-2 py-0.5 rounded-full">per meeting</span>
                </div>

                {/* Giant number */}
                <div className="relative">
                  <motion.div
                    className="font-handwritten text-6xl md:text-8xl text-red-600 leading-none"
                    key={costs.totalPerMeeting}
                    initial={{ scale: 1.1, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    {formatCurrencyValue(costs.totalPerMeeting, currency)}
                  </motion.div>

                  {/* Circle around it */}
                  <DoodleCircle className="absolute -inset-4 text-red-300 opacity-50 scale-110" />
                </div>

                {/* Breakdown - like margin notes */}
                <div className="mt-8 pt-6 border-t-2 border-dashed border-stone-200">
                  <div className="font-sketch text-sm text-stone-500 mb-4">wait, how?</div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-sketch text-stone-600 text-sm">
                        time in meeting ({attendees} x {formatCurrencyValue(costs.hourlyRate, currency)}/hr x {duration}hr)
                      </span>
                      <span className="font-handwritten text-xl text-stone-700">{formatCurrencyValue(costs.directCost, currency)}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="font-sketch text-stone-600 text-sm">
                        + recovery time (23 min each to refocus!)
                      </span>
                      <span className="font-handwritten text-xl text-orange-600">{formatCurrencyValue(costs.recoveryCost, currency)}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="font-sketch text-stone-600 text-sm">
                        + productivity tax (40% of next hour gone)
                      </span>
                      <span className="font-handwritten text-xl text-red-600">{formatCurrencyValue(costs.productivityCost, currency)}</span>
                    </div>

                    <div className="h-px bg-stone-300 my-2" style={{
                      backgroundImage: 'repeating-linear-gradient(90deg, #d6d3d1 0px, #d6d3d1 4px, transparent 4px, transparent 8px)'
                    }} />

                    <div className="flex justify-between items-center">
                      <span className="font-handwritten text-xl text-stone-800">= TRUE COST</span>
                      <span className="font-handwritten text-2xl text-red-600 underline decoration-wavy decoration-red-400">
                        {formatCurrencyValue(costs.totalPerMeeting, currency)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Yearly projection sticky note */}
                {frequency > 1 && (
                  <motion.div
                    initial={{ opacity: 0, rotate: -5, y: 20 }}
                    animate={{ opacity: 1, rotate: 3, y: 0 }}
                    className="absolute -bottom-4 -right-4"
                  >
                    <StickyNote color="yellow" rotation={5}>
                      <div className="font-sketch text-xs text-yellow-800">yearly ({frequency}x):</div>
                      <div className="font-handwritten text-2xl text-red-600">
                        {formatCurrencyValue(costs.yearlyTotal, currency)}
                      </div>
                      <div className="font-sketch text-xs text-yellow-700 mt-1">
                        {costs.yearlyTotal > curr.salaryThreshold ? '(!!! thats a salary)' : 'oof.'}
                      </div>
                    </StickyNote>
                  </motion.div>
                )}
              </div>

              {/* Action Buttons - Engage users */}
              <motion.div
                className="mt-8 grid grid-cols-2 gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {/* Start Live Meeting Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowLiveMeeting(true);
                    if (soundEnabled) soundEngine.playTick();
                  }}
                  className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-red-500 to-orange-500 text-white font-sketch rounded-lg shadow-lg hover:shadow-xl transition-all sketchy-border"
                >
                  <Timer className="w-5 h-5" />
                  <span>Track Live Meeting</span>
                </motion.button>

                {/* Generate Invoice Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowInvoice(true);
                    if (soundEnabled) soundEngine.playPaperCrumple();
                  }}
                  className="flex items-center justify-center gap-3 p-4 bg-stone-800 text-white font-sketch rounded-lg shadow-lg hover:shadow-xl transition-all sketchy-border"
                >
                  <FileText className="w-5 h-5" />
                  <span>Generate Invoice</span>
                </motion.button>
              </motion.div>

              {/* What If Optimizer */}
              <div className="mt-8">
                <WhatIfOptimizer
                  attendees={attendees}
                  duration={duration}
                  costs={costs}
                  currency={currency}
                  soundEnabled={soundEnabled}
                />
              </div>

              {/* What else could you buy - doodle style */}
              <div className="mt-8 bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-stone-200 sketchy-border">
                <div className="font-sketch text-stone-600 mb-4 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-yellow-500" />
                  instead of this meeting (yearly), you could buy:
                </div>

                <div className="flex flex-wrap gap-4">
                  {[
                    { emoji: '💻', label: 'MacBooks', divisor: 2500 },
                    { emoji: '☕', label: 'coffees', divisor: 5 },
                    { emoji: '🎫', label: 'conf tickets', divisor: 1500 },
                    { emoji: '🌴', label: 'vacation days', divisor: 500 },
                  ].map((item) => {
                    const count = Math.floor(costs.yearlyTotal / item.divisor);
                    if (count < 1) return null;
                    return (
                      <div key={item.label} className="text-center">
                        <div className="text-2xl">{item.emoji}</div>
                        <div className="font-handwritten text-2xl text-stone-800">{count}</div>
                        <div className="font-sketch text-xs text-stone-500">{item.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Research Section - Notebook margin notes style */}
      <section className="py-16 px-6 relative">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {/* Section header */}
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px flex-1 bg-stone-300" />
              <span className="font-handwritten text-2xl text-stone-600">the research says...</span>
              <div className="h-px flex-1 bg-stone-300" />
            </div>

            {/* Stats as margin notes */}
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { stat: '$37B', label: 'wasted yearly on unnecessary meetings', source: 'Harvard Business Review', link: 'https://hbr.org/2017/07/stop-the-meeting-madness', color: 'red' },
                { stat: '23 min', label: 'to refocus after ANY interruption', source: 'UC Irvine (Gloria Mark)', link: 'https://www.ics.uci.edu/~gmark/chi08-mark.pdf', color: 'orange' },
                { stat: '40%', label: 'productivity loss from context switching', source: 'APA', link: 'https://www.apa.org/topics/research/multitasking', color: 'yellow' },
                { stat: '252%', label: 'increase in meeting time since 2020', source: 'Microsoft Work Trend', link: 'https://www.microsoft.com/en-us/worklab/work-trend-index', color: 'blue' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-4 items-start"
                >
                  <div className={`font-handwritten text-4xl text-${item.color}-600`}>
                    {item.stat}
                  </div>
                  <div>
                    <div className="font-sketch text-stone-700">{item.label}</div>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-sketch text-xs text-stone-400 hover:text-red-500 underline decoration-dotted cursor-pointer"
                    >
                      - {item.source} ↗
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Cal Newport quote - sticky note */}
            <motion.div
              className="mt-12 flex justify-center"
              initial={{ opacity: 0, rotate: -2 }}
              whileInView={{ opacity: 1, rotate: -2 }}
              viewport={{ once: true }}
            >
              <StickyNote color="blue" rotation={-2} className="max-w-md">
                <div className="font-sketch text-blue-800 text-sm leading-relaxed">
                  "Engineers need <span className="underline">90+ uninterrupted minutes</span> to enter flow state.
                  A 30-min meeting doesn't cost 30 min. It costs the entire morning's potential."
                </div>
                <div className="font-sketch text-xs text-blue-600 mt-2">- Cal Newport, Deep Work</div>
              </StickyNote>
            </motion.div>

            {/* Research citation box */}
            <motion.div
              className="mt-12 bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-stone-200 sketchy-border"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <span className="font-handwritten text-xl text-stone-700">research sources</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-sketch text-stone-400">[1]</span>
                  <a
                    href="https://hbr.org/2017/07/stop-the-meeting-madness"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sketch text-stone-600 hover:text-red-500 underline decoration-dotted"
                  >
                    Harvard Business Review - "Stop the Meeting Madness" (2017) ↗
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-sketch text-stone-400">[2]</span>
                  <a
                    href="https://www.ics.uci.edu/~gmark/chi08-mark.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sketch text-stone-600 hover:text-red-500 underline decoration-dotted"
                  >
                    UC Irvine - Gloria Mark et al. "The Cost of Interrupted Work" (2008) ↗
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-sketch text-stone-400">[3]</span>
                  <a
                    href="https://www.apa.org/topics/research/multitasking"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sketch text-stone-600 hover:text-red-500 underline decoration-dotted"
                  >
                    APA - "Multitasking: Switching Costs" ↗
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-sketch text-stone-400">[4]</span>
                  <a
                    href="https://www.microsoft.com/en-us/worklab/work-trend-index"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sketch text-stone-600 hover:text-red-500 underline decoration-dotted"
                  >
                    Microsoft Work Trend Index - Annual Report ↗
                  </a>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-200">
                <p className="font-sketch text-xs text-stone-400">
                  This calculator is based on peer-reviewed research and industry reports.
                  The 23-minute recovery time is from Gloria Mark's landmark study at UC Irvine.
                  The 40% productivity tax comes from APA research on task-switching costs.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Solutions - Quick sketchy tips */}
      <section className="py-12 px-6 bg-stone-100/50">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-handwritten text-4xl text-stone-800 mb-8 text-center">
              quick fixes I scribbled down:
            </h2>

            <div className="space-y-4">
              {[
                { icon: '✂️', tip: '2-pizza rule: if you cant feed the meeting with 2 pizzas, too many people' },
                { icon: '⏱️', tip: 'default to 25 min (Parkinsons law: work expands to fill time)' },
                { icon: '🌅', tip: 'no meetings before noon (protect deep work hours)' },
                { icon: '📝', tip: 'no agenda = no meeting (if you cant write it, async it)' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4 p-4 bg-white rounded-lg sketchy-border"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="font-sketch text-stone-700">{item.tip}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Coffee className="w-12 h-12 text-stone-400 mx-auto mb-4" />

            <h2 className="font-handwritten text-3xl text-stone-800 mb-4">
              share this with your team
            </h2>

            <p className="font-sketch text-stone-600 mb-8">
              (or just leave it open during the next unnecessary meeting)
            </p>

            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied!');
              }}
              className="px-8 py-4 bg-red-500 hover:bg-red-600 text-white font-sketch rounded-full transition-colors shadow-lg hover:shadow-xl"
            >
              copy link to share
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-stone-200">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <p className="font-sketch text-stone-500">
            scribbled by{' '}
            <a href="https://twitter.com/AKJosh21" className="text-red-500 hover:underline">
              @josharsh
            </a>
            {' '}during a meeting (probably)
          </p>
          <p className="font-sketch text-xs text-stone-400">
            sources: UC Irvine, APA, Cal Newport, Microsoft, Wharton
          </p>
        </div>
      </footer>

      {/* Modal Components */}
      <AnimatePresence>
        {showLiveMeeting && (
          <LiveMeetingMode
            isOpen={showLiveMeeting}
            onClose={() => setShowLiveMeeting(false)}
            attendees={attendees}
            hourlyRate={costs.hourlyRate}
            currency={currency}
            soundEnabled={soundEnabled}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showInvoice && (
          <MeetingInvoice
            isOpen={showInvoice}
            onClose={() => setShowInvoice(false)}
            attendees={attendees}
            duration={duration}
            costs={costs}
            currency={currency}
            soundEnabled={soundEnabled}
          />
        )}
      </AnimatePresence>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="meeting-tax" variant="light" />
    </div>
  );
};

export default MeetingTaxStudy;
